import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { Subject, firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { CurrentUser } from '../../models/common/current-user';
import { FeatureItem } from '../../models/common/dynamic-form';
import { EducatorAccess, EducatorSelectedClub } from '../../models/common/educator-lookup';
import { ShowAdminAccess } from '../../models/common/show-admin-lookup';
import { ShowApi } from '../../models/common/show-api';
import { LOGOUT } from '../constants/api-endpoints';
import { SESSION_STORAGE_KEYS } from '../constants/storage-keys';
import { IndexedDbService } from './indexed-db.service';
import { toHttpParams } from '../utils/http-params.util';
import { suspendFilterWrites } from '../../shared/utils/filter-storage.util';

const LOGOUT_TIMEOUT_MS = 10_000;
const EXPIRY_LOGOUT_TIMEOUT_MS = 3_000;

export type LogoutSeverity = 'success' | 'warn';

export interface LogoutMessage {
  message: string;
  severity: LogoutSeverity;
}

/** Merged session + base functionality shared across all portals */
@Injectable({ providedIn: 'root' })
export class BaseSessionService {
  static readonly KEY_AUTH_TOKEN = 'user-auth-token';
  static readonly KEY_REFRESH_TOKEN = 'user-refresh-token';
  static readonly KEY_DISPLAY_NAME = 'user-display-name';
  static readonly KEY_PROFILE_COMPLETED = 'user-profile-completed';
  /** Interim role source for logins whose JWT doesn't yet carry a role claim. */
  static readonly KEY_ROLE = 'user-role';
  /** Authenticated user's uuid (e.g. educatorUuid), when the API supplies one. */
  static readonly KEY_UUID = 'user-uuid';
  static readonly ORG_ABBREVIATION = 'org-abbreviation';
  static readonly ORG_CONFIG = 'org-config';
  /** The full selected show object (showId, showName, showCode, isActive). */
  static readonly SELECTED_SHOW = 'selected-show';
  /** The educator's API-validated club/show mapping; enforced by educatorShowGuard. */
  static readonly EDUCATOR_ACCESS = 'educator-access';
  /** The club picked on `/educator/profile`, before a show validates it. */
  static readonly EDUCATOR_SELECTED_CLUB = 'educator-selected-club';
  /** The show-admin's API-validated show; enforced by showAdminShowGuard. */
  static readonly SHOW_ADMIN_ACCESS = 'show-admin-access';
  /** The show uuid the OTP response mapped this admin to — what `/show-admin`
   *  resolves, before `ValidationStateAdminShow` confirms it. */
  static readonly STATE_ADMIN_SHOW_UUID = 'state-admin-show-uuid';
  /** Signed-in user's email. The JWT carries it only under a schema-URI claim,
   *  so logins that need it later (the show-admin show check) store it here. */
  static readonly KEY_EMAIL = 'user-email';
  /**
   * The roleId this session was signed in with — i.e. which portal's login
   * created it. `KEY_SELECTED_ROLE_ID` is only what the user last *picked*, so
   * the two differ the moment someone chooses a different portal card.
   */
  static readonly KEY_SESSION_ROLE_ID = 'session-role-id';
  static readonly KEY_FILTERS_PREFIX = 'user-filters';
  static readonly KEY_SELECTED_ROLE_ID = 'selected-role-id';
  /** Survives the logout reload so the login screen can confirm the sign-out. */
  static readonly KEY_LOGOUT_MESSAGE = 'logout-message';
  static readonly KEY_LOGOUT_SEVERITY = 'logout-severity';
  /** Cached features fetched at login time, keyed by roleUuid. */
  static readonly KEY_ROLE_FEATURES = 'role-features';
  private static readonly CLAIM_NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';

  protected http: HttpClient = inject(HttpClient);

  private readonly _displayNameChangeSource = new Subject<string | undefined>();
  readonly displayNameChangeSource = this._displayNameChangeSource.asObservable();

  private readonly _profileCompletionChangeSource = new Subject<boolean>();
  readonly profileCompletionChangeSource = this._profileCompletionChangeSource.asObservable();

  private currentUser: CurrentUser | null = null;
  private pendingLogout?: Promise<void>;
  private indexedDb = inject(IndexedDbService);

  /** Convert object to HttpParams, filtering out null/undefined/empty */
  toHttpParams(obj: object): HttpParams {
    return toHttpParams(obj);
  }

  getSessionItem(key: string): string | undefined {
    return sessionStorage.getItem(key) ?? undefined;
  }

  setDisplayName(displayName: string | undefined): void {
    if (displayName) {
      sessionStorage.setItem(BaseSessionService.KEY_DISPLAY_NAME, displayName);
    } else {
      sessionStorage.removeItem(BaseSessionService.KEY_DISPLAY_NAME);
    }
    this._displayNameChangeSource.next(displayName);
  }

  getDisplayName(): string | undefined {
    return this.getSessionItem(BaseSessionService.KEY_DISPLAY_NAME);
  }

  /** Store interim role (until JWT carries role claim) and invalidate cached user to force re-decode. */
  setRole(role: string): void {
    sessionStorage.setItem(BaseSessionService.KEY_ROLE, role);
    this.currentUser = null;
  }

  /** Whether a non-expired JWT is present in the session. */
  isTokenValid(): boolean {
    try {
      const jwt = jwtDecode(this.getSessionItem(BaseSessionService.KEY_AUTH_TOKEN) ?? '');
      if (!jwt || !jwt.exp) {
        return false;
      }
      const expirationDate = new Date(0);
      expirationDate.setUTCSeconds(jwt.exp);
      return expirationDate > new Date();
    } catch {
      return false;
    }
  }

  /** Identity decoded from the JWT (cached) */
  getCurrentUser(): CurrentUser | null {
    if (this.currentUser) {
      return this.currentUser;
    }
    const token = this.getSessionItem(BaseSessionService.KEY_AUTH_TOKEN);
    if (!token) {
      return null;
    }
    try {
      const decoded = jwtDecode<any>(token);
      this.currentUser = {
        id: decoded.id ?? decoded.sub,
        // Prefer the JWT role; fall back to a role stored at login (interim,
        // until every login endpoint embeds a role claim in the token).
        role: decoded.role ?? this.getSessionItem(BaseSessionService.KEY_ROLE) ?? 'FAMILY',
        isProfileCompleted: false,
        familyUuid: decoded.familyUuid ?? decoded.family_uuid,
        // The API signs the email in as `ClaimTypes.Name`.
        email: decoded.email ?? decoded[BaseSessionService.CLAIM_NAME],
        familyName: decoded.familyName ?? decoded.family_name
      };
      return this.currentUser;
    } catch {
      return null;
    }
  }

  isProfileCompleted(): boolean {
    return this.getSessionItem(BaseSessionService.KEY_PROFILE_COMPLETED) === 'true';
  }

  setProfileCompleted(completed: boolean): void {
    const changed = this.isProfileCompleted() !== completed;
    sessionStorage.setItem(BaseSessionService.KEY_PROFILE_COMPLETED, completed.toString());
    if (this.currentUser) {
      this.currentUser.isProfileCompleted = completed;
    }
    // Only notify when the flag actually flips — the profile guard calls this on
    // every navigation, and an unconditional emit re-fetched the whole dashboard.
    if (changed) {
      this._profileCompletionChangeSource.next(completed);
    }
  }

  /** Get the selected roleId from session storage */
  getSelectedRoleId(): string | undefined {
    return this.getSessionItem(BaseSessionService.KEY_SELECTED_ROLE_ID);
  }

  /** Set the selected roleId in session storage */
  setSelectedRoleId(roleId: string): void {
    sessionStorage.setItem(BaseSessionService.KEY_SELECTED_ROLE_ID, roleId);
  }

  /** Which portal's login established the current session. */
  getSessionRoleId(): string | undefined {
    return this.getSessionItem(BaseSessionService.KEY_SESSION_ROLE_ID);
  }

  setSessionRoleId(roleId: string): void {
    sessionStorage.setItem(BaseSessionService.KEY_SESSION_ROLE_ID, roleId);
  }

  /** Cache the features fetched at login, tagged with the role they belong to. */
  setRoleFeatures(roleUuid: string, features: FeatureItem[]): void {
    sessionStorage.setItem(
      BaseSessionService.KEY_ROLE_FEATURES,
      JSON.stringify({ roleUuid, features })
    );
  }

  /** Cached features for the role this session signed in as. Features are
   *  role-scoped, so a cache left behind by another role is ignored. */
  getRoleFeatures(): FeatureItem[] | null {
    const cached = this.getSessionItem(BaseSessionService.KEY_ROLE_FEATURES);
    if (!cached) {
      return null;
    }
    try {
      const parsed = JSON.parse(cached) as { roleUuid: string; features: FeatureItem[] };
      return parsed.roleUuid === this.getSessionRoleId() ? parsed.features : null;
    } catch (e) {
      console.error('[session] Failed to parse cached features:', e);
      return null;
    }
  }

  /** An active cached feature by name, e.g. `FamilyProfile`. */
  getFeatureByName(featureName: string): FeatureItem | null {
    const features = this.getRoleFeatures();
    return features?.find((f) => f.featureName === featureName && f.isActive) ?? null;
  }

  /**
   * End this session and open a different portal's login.
   *
   * Each portal is a separate account, so switching cannot reuse the current
   * session. Reloads for the same reason as `logoutAndReload` — in-memory
   * service caches must not outlive the session. The roleId is written *after*
   * the wipe because every login route sits behind `roleSelectionGuard`; it is
   * the portal the user just picked, never one carried over.
   */
  async switchPortal(loginRoute: string, roleId?: string): Promise<void> {
    await this.logoutAsync();

    if (roleId) {
      this.setSelectedRoleId(roleId);
    }

    window.location.assign(loginRoute);
  }

  logout(): void {
    suspendFilterWrites();

    const refreshToken = this.getSessionItem(BaseSessionService.KEY_REFRESH_TOKEN);
    if (refreshToken) {
      void this.sendLogoutRequest(refreshToken);
    }

    this.clearSession();
  }

  logoutAsync(): Promise<void> {
    this.pendingLogout ??= this.performLogout().finally(() => {
      this.pendingLogout = undefined;
    });
    return this.pendingLogout;
  }

  private async performLogout(): Promise<void> {
    suspendFilterWrites();

    const refreshToken = this.getSessionItem(BaseSessionService.KEY_REFRESH_TOKEN);
    if (refreshToken) {
      await this.sendLogoutRequest(refreshToken);
    }

    this.clearSession();
  }

  private async sendLogoutRequest(refreshToken: string, timeoutMs = LOGOUT_TIMEOUT_MS): Promise<void> {
    try {
      const reply = await firstValueFrom(
        this.http
          .post<{ success?: unknown } | null>(LOGOUT('tsp'), { refreshToken })
          .pipe(timeout(timeoutMs))
      );

      if (reply?.success === false) {
        console.error('[logout] The server did not confirm the logout:', reply);
      }
    } catch (error) {
      console.error('[logout] The logout request failed; clearing the local session anyway:', error);
    }
  }

  private clearSession(): void {
    this.clearBrowserStorage();
    this.currentUser = null;
    this.setDisplayName(undefined);
  }

  /** Everything the app keeps in the browser: session and local storage,
   *  cookies it can reach, Cache Storage and IndexedDB. Run only at sign-out or
   *  session expiry, never during normal navigation. */
  clearBrowserStorage(): void {
    sessionStorage.clear();

    try {
      localStorage.clear();
    } catch (error) {
      console.error('[logout] Could not clear local storage:', error);
    }

    this.clearCookies();

    if (typeof caches !== 'undefined') {
      caches
        .keys()
        .then((names) => Promise.all(names.map((name) => caches.delete(name))))
        .catch((error) => console.error('[logout] Could not clear Cache Storage:', error));
    }

    this.indexedDb.clearDatabase().catch((error) => {
      console.error('[logout] Could not clear the local cache:', error);
    });
  }

  private clearCookies(): void {
    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter(Boolean)
      .forEach((name) => {
        document.cookie = `${name}=; ${expired}`;
        document.cookie = `${name}=; ${expired}; domain=${location.hostname}`;
      });
  }

  /**
   * Sign out with a **full document reload** rather than a router navigation.
   *
   * Clearing sessionStorage cannot reach in-memory service state, and the show
   * portals are lazy modules whose providers (`ExhibitorService`,
   * `ShowPortalService`) live for the lifetime of the app. Their `shareReplay`
   * caches — the exhibitor roster in particular — would otherwise be replayed to
   * whoever logs in next, leaking one account's data to another. Reloading
   * discards the whole JS heap, so no cache can outlive the session.
   */
  async logoutAndReload(loginRoute: string, message?: string): Promise<void> {
    // The selected role is deliberately NOT preserved: it would then never be
    // cleared, so the next person to use this tab would reach any login screen
    // carrying the previous user's role. roleSelectionGuard bounces to the
    // landing page instead, which is where the message is picked up.
    await this.logoutAsync();

    if (message) {
      // Set after logout(), which clears sessionStorage.
      this.setLogoutMessage(message, 'success');
    }

    window.location.assign(loginRoute);
  }

  /** Sign-out for an expired session. An inactivity expiry does not revoke the
   *  refresh token, so a best-effort Logout goes out first; its outcome never
   *  blocks the sign-out. The warning survives the reload. */
  clearExpiredSession(message: string): void {
    suspendFilterWrites();

    const refreshToken = this.getSessionItem(BaseSessionService.KEY_REFRESH_TOKEN);
    if (refreshToken) {
      void this.sendLogoutRequest(refreshToken, EXPIRY_LOGOUT_TIMEOUT_MS);
    }

    this.clearSession();
    this.setLogoutMessage(message, 'warn');
  }

  /** Waits briefly for the Logout call before the reload, which would otherwise
   *  cancel it mid-flight. */
  async expireSessionAndReload(loginRoute: string, message: string): Promise<void> {
    suspendFilterWrites();

    const refreshToken = this.getSessionItem(BaseSessionService.KEY_REFRESH_TOKEN);
    if (refreshToken) {
      await this.sendLogoutRequest(refreshToken, EXPIRY_LOGOUT_TIMEOUT_MS);
    }

    this.clearSession();
    this.setLogoutMessage(message, 'warn');
    window.location.assign(loginRoute);
  }

  private setLogoutMessage(message: string, severity: LogoutSeverity): void {
    sessionStorage.setItem(BaseSessionService.KEY_LOGOUT_MESSAGE, message);
    sessionStorage.setItem(BaseSessionService.KEY_LOGOUT_SEVERITY, severity);
  }

  /**
   * Drop everything that scopes a session to a portal, show or club.
   *
   * Signing in does not clear sessionStorage, so without this a second user in
   * the same tab inherits the first user's show, club and validated educator
   * access — enough to pass `educatorShowGuard` and query another educator's
   * club. Call it before writing a newly authenticated session.
   *
   * Keys owned by CoreService / FamilyPortalService are literals here to avoid a
   * circular import, the same way IndexedDbService handles `org-abbreviation`.
   */
  clearPortalScope(): void {
    [
      BaseSessionService.EDUCATOR_ACCESS,
      BaseSessionService.EDUCATOR_SELECTED_CLUB,
      BaseSessionService.SHOW_ADMIN_ACCESS,
      BaseSessionService.STATE_ADMIN_SHOW_UUID,
      BaseSessionService.KEY_EMAIL,
      BaseSessionService.ORG_ABBREVIATION,
      BaseSessionService.ORG_CONFIG,
      BaseSessionService.SELECTED_SHOW,
      BaseSessionService.KEY_UUID,
      BaseSessionService.KEY_ROLE,
      BaseSessionService.KEY_PROFILE_COMPLETED,
      BaseSessionService.KEY_SESSION_ROLE_ID,
      SESSION_STORAGE_KEYS.CLUB_ID,
      SESSION_STORAGE_KEYS.EDUCATOR_USER_INFO,
      SESSION_STORAGE_KEYS.EVENT_VALIDATION_FLAGS, // Clear event validation flags when exiting show
      'user-family-id',
      'user-family-fourh-county',
      'user-family-location'
    ].forEach((key) => sessionStorage.removeItem(key));

    // Saved table filters are per-user too. Clear both old-style filter keys
    // (user-filters prefix) and new-style keys (*.filters pattern).
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(BaseSessionService.KEY_FILTERS_PREFIX) || key.endsWith('.filters'))
      .forEach((key) => sessionStorage.removeItem(key));

    this.setDisplayName(undefined);
    this.currentUser = null;
  }

  /** Reads and consumes the post-logout confirmation, if one is pending. */
  takeLogoutMessage(): LogoutMessage | null {
    const message = sessionStorage.getItem(BaseSessionService.KEY_LOGOUT_MESSAGE);
    const severity = sessionStorage.getItem(BaseSessionService.KEY_LOGOUT_SEVERITY);
    sessionStorage.removeItem(BaseSessionService.KEY_LOGOUT_MESSAGE);
    sessionStorage.removeItem(BaseSessionService.KEY_LOGOUT_SEVERITY);
    return message ? { message, severity: severity === 'warn' ? 'warn' : 'success' } : null;
  }

  /** Get logo URL by org abbreviation */
  getLogoUrlByOrgAbbreviation(orgAbbreviation: string): string {
    switch (orgAbbreviation) {
      case 'sa':
        return 'assets/images/sa/san-antonio-hat-icon.svg';
      case 'fourh':
        return 'assets/images/4h/4h-logo.png';
      default:
        return '';
    }
  }

  /**
   * Retrieve the full selected show object from session storage — a GetShows
   * row (`showUuid`, `showCode`, `showName`, `isActive`), as written when the
   * portal was entered.
   */
  getSelectedShow(): ShowApi | null {
    const raw = this.getSessionItem(BaseSessionService.SELECTED_SHOW);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as ShowApi;
    } catch {
      return null;
    }
  }

  /**
   * Store the selected show object in session storage. Typed so the three
   * portals that write it can't disagree on the shape its one reader expects.
   */
  setSelectedShow(show: ShowApi): void {
    sessionStorage.setItem(BaseSessionService.SELECTED_SHOW, JSON.stringify(show));
  }

  /**
   * The club/show mapping the API confirmed for this educator. Written only
   * after `ValidateEducatorShowClubMapping` returns true, so `educatorShowGuard`
   * can treat its presence as proof of access.
   */
  getEducatorAccess(): EducatorAccess | null {
    const raw = this.getSessionItem(BaseSessionService.EDUCATOR_ACCESS);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as EducatorAccess;
    } catch {
      return null;
    }
  }

  setEducatorAccess(access: EducatorAccess): void {
    sessionStorage.setItem(BaseSessionService.EDUCATOR_ACCESS, JSON.stringify(access));
    // Notify subscribers that profile completion status may have changed
    this._profileCompletionChangeSource.next(true);
  }

  /**
   * The club picked on the setup profile page. Only half of the scope: the show
   * page validates this club against a show before writing `EducatorAccess`.
   */
  getEducatorSelectedClub(): EducatorSelectedClub | null {
    const raw = this.getSessionItem(BaseSessionService.EDUCATOR_SELECTED_CLUB);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as EducatorSelectedClub;
    } catch {
      return null;
    }
  }

  setEducatorSelectedClub(club: EducatorSelectedClub): void {
    sessionStorage.setItem(BaseSessionService.EDUCATOR_SELECTED_CLUB, JSON.stringify(club));
    // Notify subscribers that profile completion status may have changed
    this._profileCompletionChangeSource.next(true);
  }

  /**
   * The show `ValidationStateAdminShow` confirmed for this admin. Written only
   * on a `true` verdict, so `showAdminShowGuard` treats it as proof of access.
   */
  getShowAdminAccess(): ShowAdminAccess | null {
    const raw = this.getSessionItem(BaseSessionService.SHOW_ADMIN_ACCESS);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as ShowAdminAccess;
    } catch {
      return null;
    }
  }

  setShowAdminAccess(access: ShowAdminAccess): void {
    sessionStorage.setItem(BaseSessionService.SHOW_ADMIN_ACCESS, JSON.stringify(access));
  }

  /** The show uuid the OTP response mapped this admin to. */
  getStateAdminShowUuid(): string | undefined {
    return this.getSessionItem(BaseSessionService.STATE_ADMIN_SHOW_UUID);
  }

  setStateAdminShowUuid(showUuid: string | undefined): void {
    if (showUuid) {
      sessionStorage.setItem(BaseSessionService.STATE_ADMIN_SHOW_UUID, showUuid);
    } else {
      sessionStorage.removeItem(BaseSessionService.STATE_ADMIN_SHOW_UUID);
    }
  }

  getEmail(): string | undefined {
    return this.getSessionItem(BaseSessionService.KEY_EMAIL);
  }

  setEmail(email: string | undefined): void {
    if (email) {
      sessionStorage.setItem(BaseSessionService.KEY_EMAIL, email);
    } else {
      sessionStorage.removeItem(BaseSessionService.KEY_EMAIL);
    }
  }
}
